import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import Icons from '@aurora/shared-client/icons';
import { ConversationStyle } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import type { KudosCountFragment, MessageViewFragment } from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';

interface Props {
  /**
   * The message the Kudos belong to.
   */
  message: KudosCountFragment;
  /**
   * Display the icon after the kudos number.
   */
  isIconLast?: boolean;
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * Display count and text in table format.
   */
  useTable?: boolean;
  /**
   * Set a custom element for kudos count component.
   */
  countAs?: React.ElementType;
  /**
   * Class name(s) to apply to the span element.
   */
  textClassName?: string;
  /**
   * Class name(s) to apply to the counts span element.
   */
  countClassName?: string;
}

/**
 * Kudos icon and count.
 *
 * @author Jonathan Bridges
 */
const KudosCount: React.FC<React.PropsWithChildren<Props>> = ({
  message,
  isIconLast = false,
  className,
  useTable = false,
  countAs: Component = 'span',
  textClassName,
  countClassName
}) => {
  const cx = useClassNameMapper();
  const {
    FormattedMessage,
    formatMessage,
    loading: textLoading
  } = useTranslation(EndUserComponent.KUDOS_COUNT);
  const { formatNumber } = useIntl();

  if (textLoading) {
    return null;
  }

  const {
    kudosSumWeight,
    board: { conversationStyle }
  } = message as MessageViewFragment;

  const finalKudos = formatNumber(kudosSumWeight, { notation: 'compact', compactDisplay: 'short' });

  return (
    <div
      className={cx('lia-g-count-wrap', { 'lia-g-count-use-text': useTable }, className)}
      data-testid="kudosCount"
    >
      {useTable ? (
        <FormattedMessage
          id="textTitle"
          values={{
            count: finalKudos,
            messageType: conversationStyle,
            spanText: function renderChunks(chunks): React.ReactNode {
              return <span className={cx('lia-g-count-text', textClassName)}>{chunks}</span>;
            },
            spanKudos: function renderCount(): React.ReactNode {
              return (
                <Component className={cx('lia-g-count-number', countClassName)}>
                  {finalKudos}
                  <span className={cx('sr-only')}>
                    {formatMessage('likes', { count: kudosSumWeight })}
                  </span>
                </Component>
              );
            }
          }}
        />
      ) : (
        <>
          <Icon
            icon={conversationStyle === ConversationStyle.Idea ? Icons.ArrowUpIcon : Icons.LikeIcon}
            color={IconColor.GRAY_600}
            size={IconSize.PX_16}
            className={cx({ 'order-1': isIconLast })}
          />
          {kudosSumWeight}
          <span className={cx('sr-only')}>{formatMessage('likes', { count: kudosSumWeight })}</span>
        </>
      )}
    </div>
  );
};

export default KudosCount;
